import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { appServices } from '@/data/AppServices';
import type { InstalledCorpusPack, PackInstallPreview, PackUninstallPreview } from '@/domain/Models';

export function CorpusManagementPage() {
  const [packs, setPacks] = useState<InstalledCorpusPack[]>([]);
  const [preview, setPreview] = useState<PackInstallPreview | null>(null);
  const [uninstall, setUninstall] = useState<PackUninstallPreview | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const reload = useCallback(async () => {
    setPacks(await appServices.corpus.listInstalledPacks());
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const mb = packs.reduce((n, p) => n + p.installedSizeBytes, 0) / (1024 * 1024);

  return (
    <div className="page stack">
      <div className="muted">
        已安装 {packs.length} 个语料包 · 占用 {mb.toFixed(0)} MB
      </div>
      {msg && <div className="card">{msg}</div>}
      <div className="row" style={{ gap: 8 }}>
        <button type="button" onClick={() => inputRef.current?.click()}>
          安装语料包
        </button>
        <button type="button" className="secondary" onClick={() => navigate('/settings')}>
          返回
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept=".besspack,.bessarticle,application/zip"
        style={{ display: 'none' }}
        onChange={(e) => {
          const files = e.target.files ? Array.from(e.target.files) : [];
          e.target.value = '';
          if (files.length) void inspect(files);
        }}
      />

      {preview && (
        <section className="card stack">
          <strong>安装预览</strong>
          <div className="muted">
            新增 {preview.contentAdded} · 升级 {preview.contentUpdated} · 复用 {preview.contentReused}
          </div>
          {preview.errors.length > 0 && <div className="error-box">{preview.errors.join('；')}</div>}
          <button
            type="button"
            disabled={preview.errors.length > 0}
            onClick={() => void commit()}
          >
            确认安装
          </button>
          <button
            type="button"
            className="secondary"
            onClick={() => {
              void appServices.corpus.discardInstall(preview.planId);
              setPreview(null);
            }}
          >
            取消
          </button>
        </section>
      )}

      {uninstall && (
        <section className="card stack">
          <strong>卸载 {uninstall.title}？</strong>
          <div className="muted">
            预计释放 {(uninstall.estimatedFreeableBytes / (1024 * 1024)).toFixed(1)} MB · 将暂时不可学{' '}
            {uninstall.itemsBecomingUnavailable} 项
          </div>
          {uninstall.requiredPack && <div className="error-box">基础包不能卸载</div>}
          <button
            type="button"
            disabled={uninstall.requiredPack || uninstall.blockedByDependents.length > 0}
            onClick={() => void confirmUninstall()}
          >
            确认卸载
          </button>
          <button type="button" className="secondary" onClick={() => setUninstall(null)}>
            取消
          </button>
        </section>
      )}

      {packs.length === 0 && <div className="muted">还没有已安装的语料包。</div>}
      {packs.map((pack) => (
        <section key={pack.packageId} className="card stack">
          <strong>{pack.title}</strong>
          <div className="muted">
            {pack.contentVersion} · {pack.required ? '基础包' : '扩展包'} · {pack.enabled ? '已启用' : '已停用'}
          </div>
          <div className="row" style={{ gap: 8 }}>
            {!pack.required && (
              <button
                type="button"
                className="secondary"
                onClick={() => void toggle(pack)}
              >
                {pack.enabled ? '停用' : '启用'}
              </button>
            )}
            {!pack.required && (
              <button type="button" className="secondary" onClick={() => void askUninstall(pack.packageId)}>
                卸载
              </button>
            )}
          </div>
        </section>
      ))}
    </div>
  );

  async function inspect(files: File[]) {
    const r = await appServices.corpus.inspectPackages(files);
    if ('ok' in r && r.ok === false) {
      setMsg(r.message);
      return;
    }
    setPreview(r as PackInstallPreview);
  }

  async function commit() {
    if (!preview) return;
    const r = await appServices.corpus.commitInstall(preview.planId);
    setMsg(r.message);
    setPreview(null);
    await reload();
  }

  async function toggle(pack: InstalledCorpusPack) {
    const r = await appServices.corpus.setPackEnabled(pack.packageId, !pack.enabled);
    setMsg(r.message);
    await reload();
  }

  async function askUninstall(packageId: string) {
    const r = await appServices.corpus.inspectUninstall(packageId);
    if ('ok' in r && r.ok === false) {
      setMsg(r.message);
      return;
    }
    setUninstall(r as PackUninstallPreview);
  }

  async function confirmUninstall() {
    if (!uninstall) return;
    const r = await appServices.corpus.confirmUninstall(uninstall.previewId);
    setMsg(r.message);
    setUninstall(null);
    await reload();
  }
}
